import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatIcon } from "@angular/material/icon";
import { MatRadioButton, MatRadioGroup } from "@angular/material/radio";
import { MatTooltip } from "@angular/material/tooltip";
import type { InterruptItem } from "../../../model/interrupt.types";
import { HitlAskContextComponent } from "../hitl-ask-context/hitl-ask-context.component";
import { NotificationCardComponent } from "../../shared/notification-card/notification-card.component";

/**
 * One serialized weak-candidate row from `metadata.binding_detail` — the
 * per-candidate judge reasoning the engine stamps on low-confidence asks
 * (option ids are evidence ids, so rows map 1:1 onto options).
 */
interface BindingCandidate {
  role?: string;
  evidence_id: string;
  label?: string;
  confidence?: number;
  reasoning?: string;
}

type CardState = "pending" | "resolved" | "expired";

const TIMEOUT_MS = 5 * 60 * 1000;

/**
 * HITL card for kind === 'select' and 'multiselect'.
 * Single-select submits immediately on click; multiselect requires a Confirm button.
 * Emits a single option id (string) or an array of ids (string[]).
 */
@Component({
  selector: "app-hitl-selection-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButton,
    MatCheckbox,
    MatIcon,
    MatRadioButton,
    MatRadioGroup,
    MatTooltip,
    HitlAskContextComponent,
    NotificationCardComponent,
  ],
  templateUrl: "./hitl-selection-card.component.html",
  styleUrl: "./hitl-selection-card.component.scss",
})
export class HitlSelectionCardComponent implements OnInit {
  entry = input.required<InterruptItem>();

  readonly submitted = output<string | string[]>();

  protected readonly state = signal<CardState>("pending");

  protected readonly selectedIds = signal<string[]>([]);

  protected readonly resolvedLabels = signal<string[]>([]);

  protected readonly isMulti = computed(() => this.entry().kind === "multiselect");

  protected readonly canConfirm = computed(() => this.selectedIds().length > 0);

  /** binding_detail rows keyed by evidence_id (= option id) for option tooltips; empty without metadata. */
  protected readonly candidatesById = computed(() => {
    const meta = (this.entry().metadata ?? {}) as Record<string, unknown>;
    const detail = meta.binding_detail as { candidates?: BindingCandidate[] } | undefined;
    const rows = Array.isArray(detail) ? detail : Array.isArray(detail?.candidates) ? detail!.candidates! : [];
    const byId = new Map<string, BindingCandidate>();
    for (const row of rows) {
      if (row && typeof row.evidence_id === "string") byId.set(row.evidence_id, row);
    }
    return byId;
  });

  /** Human tooltip for an option: label + confidence + judge reasoning; null when nothing is stamped. */
  protected tooltipFor(optionId: string): string | null {
    const candidate = this.candidatesById().get(optionId);
    if (!candidate) return null;
    const confidence =
      typeof candidate.confidence === "number" ? ` (confidence ${candidate.confidence.toFixed(2)})` : "";
    const reasoning = candidate.reasoning?.trim() ?? "";
    const text = `${candidate.label ?? optionId}${confidence}${reasoning ? ` — ${reasoning}` : ""}`.trim();
    return text.length > 0 ? text : null;
  }

  readonly #destroyRef = inject(DestroyRef);

  private timeoutHandle?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.timeoutHandle = setTimeout(() => this.expire(), TIMEOUT_MS);
    this.#destroyRef.onDestroy(() => clearTimeout(this.timeoutHandle));
  }

  protected selectOne(id: string): void {
    if (this.state() !== "pending") return;
    clearTimeout(this.timeoutHandle);
    const opt = this.entry().options.find((o) => o.id === id);
    this.resolvedLabels.set(opt ? [opt.label] : [id]);
    this.state.set("resolved");
    this.submitted.emit(id);
  }

  protected toggleMultiId(id: string): void {
    this.selectedIds.update((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  protected confirmMulti(): void {
    if (!this.canConfirm()) return;
    clearTimeout(this.timeoutHandle);
    const ids = this.selectedIds();
    const labels = this.entry()
      .options.filter((o) => ids.includes(o.id))
      .map((o) => o.label);
    this.resolvedLabels.set(labels);
    this.state.set("resolved");
    this.submitted.emit(ids);
  }

  protected restart(): void {
    clearTimeout(this.timeoutHandle);
    this.selectedIds.set([]);
    this.state.set("pending");
    this.timeoutHandle = setTimeout(() => this.expire(), TIMEOUT_MS);
  }

  private expire(): void {
    this.state.set("expired");
  }
}
